<template>
  <v-container class="mx-0">
    <v-row class="py-2" align="center" justify="center">
      <v-btn class="mx-2" fab dark x-small color="grey-navy" @click="clickUpdateWeek(-1)">
        <v-icon dark>
          mdi-minus
        </v-icon>
      </v-btn>
      <h2>
        {{ firstDay | moment("DD MMMM") }}
        -
        {{ lastDay | moment("DD MMMM YYYY") }}
      </h2>
      <v-btn class="mx-2" fab dark x-small color="grey-navy" @click="clickUpdateWeek(1)">
        <v-icon dark>
          mdi-plus
        </v-icon>
      </v-btn>
    </v-row>
    <div v-if="!loading">
      <v-row v-for="day in days" :key="day">
        <v-col cols="2">
          <h2 class="secondary--text font-weight-bold">{{ day }}</h2>
          <div v-for="item in getDayActions(day)" :key="item.key">
            <h3>
              <a
                v-if="item.node && item.node.workout.id"
                class="grey-navy--text"
                :href="'https://clients.fitnessfaculty.club/test/workout/' + item.node.workout.id"
                target="_blank"
              >
                {{ item.node.workout.label }}
              </a>
              <a
                v-else-if="['WORKOUT', 'ED', 'COURSE'].includes(item.type)"
                class="grey-navy--text"
                :href="$appEndPoint + (item.type == 'COURSE' ? 'program/' : 'unit-activity/') + item.id"
                target="_blank"
              >
                {{ capitalize(item.name) }}
              </a>
              <span v-else class="grey-navy--text">
                {{ capitalize(item.name) }}
              </span>
            </h3>
          </div>
        </v-col>
        <v-col cols="10" v-if="!$apollo.loading">
          <v-card class="pa-2" outlined tile>
            <v-card-text class="pa-1">
              <v-tooltip v-for="activity in getDayActivities(day)" :key="activity.node.id" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div>
                    <v-chip v-bind="attrs" v-on="on" class="my-2 mx-2" :color="getNotificationColor(activity.node)">
                      {{ activityTitle(activity.node) }}
                    </v-chip>

                    <v-btn
                      v-if="coachingChatId"
                      small
                      @click="
                        chatDialog = true;
                        selectedActivityImage = activity.node.image;
                        selectedActivityId = activity.node.id;
                        selectedCoachComment = activity.node.coachComment;
                        selectedLog = activity.node;
                      "
                      ><v-icon color="navy">mdi-chat</v-icon></v-btn
                    >
                  </div>
                </template>
                <div>
                  <span class="block">
                    Logged:
                    {{ activity.node.createdAt | moment("HH:MM, ddd DD MMM") }}
                  </span>
                  <span v-if="activity.node.type && activity.node.type.name"
                    >Type: {{ activity.node.type.name }} <br
                  /></span>
                  <span v-if="activity.node.calories">Calories: {{ activity.node.calories }} <br /></span>
                  <span v-if="activity.node.seconds"> Time: {{ activity.node.seconds | humanizeSeconds }}<br /></span>
                  <span v-if="activity.node.heartRate">Heart rate: {{ activity.node.heartRate }}<br /></span>
                  <span v-if="activity.node.distance">Distance: {{ activity.node.distance }}<br /></span>
                  <span v-if="activity.node.effort">Effort: {{ activity.node.effort }}<br /></span>
                  <div v-if="getActivityComment(activity.node)">
                    Comment: <br />
                    {{ getActivityComment(activity.node) }}
                  </div>
                  <img
                    v-if="activity.node.image"
                    :height="600"
                    :src="activity.node.image"
                    :lazy-src="activity.node.image"
                  />
                </div>
              </v-tooltip>
              <v-tooltip bottom v-if="getRestDay(day)">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip v-bind="attrs" v-on="on">
                    Rest Day
                  </v-chip>
                </template>
                <div>
                  Planned: {{ getRestDay(day).node.planned ? "Yes" : "No" }}
                  <br />
                  Details: {{ getRestDay(day).node.details || "None" }}
                </div>
              </v-tooltip>
              <v-btn
                v-if="coachingChatId && getRestDay(day)"
                class="ml-2"
                small
                @click="
                  chatDialog = true;
                  selectedActivityId = getRestDay(day).node.id;
                  selectedCoachComment = getRestDay(day).node.coachComment;
                  selectedLog = getRestDay(day).node;
                "
                ><v-icon color="navy">mdi-chat</v-icon></v-btn
              >
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-dialog
        v-if="chatDialog"
        persistent
        v-model="chatDialog"
        width="400"
        class="limited-height"
        transition="dialog-bottom-transition"
      >
        <send-message-card
          @closed="chatDialog = false"
          :coaching-chat-id="coachingChatId"
          :image="selectedActivityImage"
          :log-id="selectedActivityId"
          log-type="ACTIVITY"
          :user-id="userId"
          :coach-comment="selectedCoachComment"
          :log="selectedLog"
        />
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { USER_ACTIVITIES_QUERY } from "@/constants/graphql/training_log/activity";
import { SUBSCRIPTION_ACTIVITY_QUERY } from "@/constants/graphql/coach/coaching";
import { capitalize } from "@/lib/string";
import SendMessageCard from "@/components/chats/SendMessageCard";
import WorkoutBlockMixin from "@/components/shared/mixins/WorkoutBlockMixin.js";

export default {
  name: "TrainingLog",
  mixins: [WorkoutBlockMixin],
  components: {
    SendMessageCard,
  },
  props: {
    userId: {
      type: String,
      default: null,
    },
    selectedDate: {
      type: String,
      required: false,
    },
    coachingChatId: {
      type: [Number, String],
      required: false,
    },
  },
  data() {
    return {
      activities: [],
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      lastDay: "",
      firstDay: "",
      actions: [],
      restDays: [],
      workoutBlockWorkoutLogs: [],
      loading: true,
      workoutBlockEvents: [],
      chatDialog: false,
      selectedActivityImage: null,
      selectedActivityId: null,
      selectedCoachComment: null,
      selectedLog: null,
    };
  },
  mounted() {
    this.resetDate();
  },
  watch: {
    userId: function() {
      this.resetDate();
    },
    selectedDate: function() {
      this.resetDate();
    },
  },
  methods: {
    getActivityComment(node) {
      if (node.comment) {
        return node.comment;
      }
      if (node.comments) {
        return node.comments;
      }
      return null;
    },
    capitalize(string) {
      return capitalize(string);
    },
    resetDate() {
      var firstDay = new Date(this.getMonday()).toISOString();
      var lastDay = new Date(this.getMonday());
      lastDay.setDate(lastDay.getDate() + 6);
      lastDay = lastDay.toISOString();
      this.firstDay = firstDay;
      this.lastDay = lastDay;
    },
    clickUpdateWeek(interval) {
      this.$emit("update-week", interval);
      this.updateWeek(interval);
    },
    updateWeek(interval) {
      let value = interval * 7;
      let firstDay = new Date(this.firstDay);
      let lastDay = new Date(this.lastDay);
      firstDay.setDate(firstDay.getDate() + value);
      lastDay.setDate(lastDay.getDate() + value);
      this.firstDay = firstDay;
      this.lastDay = lastDay;
    },
    getMonday() {
      var startDate = new Date();
      if (this.selectedDate) {
        startDate = new Date(this.selectedDate);
      }

      startDate.setHours(0, 0, 0, 0);
      var day = startDate.getDay(),
        diff = startDate.getDate() - day + (day == 0 ? -6 : 1);
      return new Date(startDate.setDate(diff));
    },
    logItem(activity) {
      var activityItem = {};
      if (activity.workout) {
        activityItem = activity.workout;
      } else if (activity.action) {
        activityItem = activity.action.workout;
      } else if (activity.event) {
        activityItem = activity.event;
      } else if (activity.guidedRun) {
        activityItem = activity.guidedRun;
      }
      return activityItem;
    },
    getNotificationColor(activity) {
      if (activity.isCustom) {
        return "exercise darken-1";
      }
      if (activity.event) {
        if (activity.event.eventType) {
          return activity.event.eventType.category.toLowerCase();
        }
      } else if (activity.workout || activity.action || activity.guidedRun) {
        return "exercise lighten-1";
      } else if (activity.isCustom && activity.type) {
        return activity.type.category.toLowerCase();
      }
      if (activity.unitActivity) {
        if (activity.unitActivity.type == "MODULE" || activity.unitActivity.type == "ED") {
          return "mindset";
        }
        return "exercise lighten-1";
      }
    },
    getDayActivities(day) {
      let activityLogs = this.activities.filter(
        (activity) => this.$moment(activity.node.startDate).format("dddd") == day
      );
      let blockLogs = this.workoutBlockWorkoutLogs.filter(
        (log) => this.$moment(log.node.startDate).format("dddd") == day
      );

      return [...blockLogs, ...activityLogs];
    },
    getDayActions(day) {
      const actionDate = this.getDateforDay(day, this.firstDay);
      const actions = this.actions.filter((action) => this.$moment(action.node.date).format("dddd") == day);

      const filteredWorkoutBlockEvents = this.workoutBlockEvents.filter(
        (event) => event.start == actionDate.format("YYYY-MM-DD") && event.type != "PROGRAM"
      );

      let combined = [...actions, ...filteredWorkoutBlockEvents];
      combined.map((obj) => ({ ...obj, key: actionDate }));
      return combined;
    },
    activityTitle(activity) {
      if (activity.unitActivity) {
        return activity.unitActivity.activity.title + " | " + activity.subscriptionBlock.block.title + " (Block)";
      }

      if (activity.isCustom) {
        if (activity.type) {
          if (activity.type.name.toLowerCase() == "other") {
            return activity.activityType + " | Custom Activity";
          }
          return activity.type.name + " | Custom Activity";
        }
        return "Other | " + activity.activityType.replace("Other: ", "") + " | Custom Activity";
      } else {
        if (activity.event) {
          return activity.event.name;
        } else if (activity.workout || activity.action) {
          return this.logItem(activity).label;
        } else {
          return this.logItem(activity).name;
        }
      }
    },
    getRestDay(day) {
      return this.restDays.find(
        (rest) => this.$moment(rest.node.restDayDate).format("dddd") == day && rest.node.didRest
      );
    },
  },
  apollo: {
    activities: {
      query: USER_ACTIVITIES_QUERY,
      variables() {
        return {
          userId: btoa("UserNode:" + this.userId),
          startDate: this.$moment(this.firstDay).format("YYYY-MM-DD"),
          endDate: this.$moment(this.lastDay).format("YYYY-MM-DD"),
        };
      },
      skip() {
        return !this.userId || !this.firstDay || !this.lastDay;
      },
      result(response) {
        if (response.data) {
          this.loading = false;
          this.workoutBlockWorkoutLogs = response.data.workoutBlockWorkoutLogs.edges;
          this.actions = response.data.actions.edges;
          this.activities = response.data.activities.edges;
          this.restDays = response.data.restDays.edges;
        }
      },
    },
    workoutSubscriptionBlocks: {
      query: SUBSCRIPTION_ACTIVITY_QUERY,
      variables() {
        return {
          userId: btoa("UserNode:" + this.userId),
          startDate_Lte: this.$moment(this.lastDay).format("YYYY-MM-DD"),
          endDate_Gte: this.$moment(this.lastDay).format("YYYY-MM-DD"),
        };
      },
      skip() {
        return !this.userId || !this.firstDay || !this.lastDay;
      },
      result(response) {
        if (response.data && !response.loading) {
          const edges = response.data.workoutSubscriptionBlocks.edges;
          this.workoutBlockEvents = this.getWorkoutBlockEvents(edges);
        }
      },
    },
  },
};
</script>
